<template>
  <div
    v-if="invoiceId"
    class="eft-proof-modal-backdrop"
    @click.self="close"
  >
    <div
      class="eft-proof-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="eft-proof-title"
    >
      <div class="eft-proof-modal__header">
        <div>
          <h2 id="eft-proof-title" class="h5 mb-0">Proof of payment</h2>
          <p v-if="filename" class="eft-proof-modal__file mb-0">{{ filename }}</p>
        </div>
        <button type="button" class="btn-close" aria-label="Close" @click="close" />
      </div>
      <div class="eft-proof-modal__body">
        <p v-if="loading" class="mb-0">Loading proof…</p>
        <p v-else-if="error" class="text-danger mb-0">{{ error }}</p>
        <img
          v-else-if="isImage && url"
          class="eft-proof-modal__image"
          :src="url"
          alt="Proof of payment"
        />
        <iframe
          v-else-if="url"
          class="eft-proof-modal__frame"
          :src="url"
          title="Proof of payment"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { closeEftProof, eftProofViewer, loadEftProof } from '@/utils/eftProof'

export default {
  name: 'EftProofModal',
  data() {
    return {
      url: '',
      contentType: '',
      loading: false,
      error: '',
    }
  },
  computed: {
    invoiceId() {
      return eftProofViewer.invoiceId
    },
    filename() {
      return eftProofViewer.filename
    },
    nonce() {
      return eftProofViewer.nonce
    },
    isImage() {
      if (String(this.contentType || '').startsWith('image/')) return true
      return /\.(png|jpe?g|webp|gif)$/i.test(this.filename || '')
    },
  },
  watch: {
    nonce: {
      immediate: true,
      handler() {
        this.load()
      },
    },
  },
  mounted() {
    window.addEventListener('keydown', this.onKeydown)
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeydown)
    this.revoke()
  },
  methods: {
    onKeydown(event) {
      if (event.key === 'Escape' && this.invoiceId) this.close()
    },
    revoke() {
      if (this.url) URL.revokeObjectURL(this.url)
      this.url = ''
    },
    close() {
      this.revoke()
      this.error = ''
      this.loading = false
      closeEftProof()
    },
    async load() {
      const id = this.invoiceId
      this.revoke()
      this.error = ''
      this.contentType = ''
      if (!id) {
        this.loading = false
        return
      }
      this.loading = true
      try {
        const proof = await loadEftProof(id)
        if (this.invoiceId !== id) {
          if (proof?.url) URL.revokeObjectURL(proof.url)
          return
        }
        this.url = proof.url
        this.contentType = proof.contentType
      } catch (err) {
        if (this.invoiceId === id) {
          this.error = err.response?.data?.detail || err.message || 'Could not open proof of payment.'
        }
      } finally {
        if (this.invoiceId === id) this.loading = false
      }
    },
  },
}
</script>

<style>
.eft-proof-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 5000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(15, 23, 42, 0.55);
}

.eft-proof-modal {
  display: flex;
  flex-direction: column;
  width: min(920px, 96vw);
  max-height: min(90vh, 56rem);
  overflow: hidden;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 48px rgba(15, 23, 42, 0.22);
}

.eft-proof-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.15rem;
  border-bottom: 1px solid rgba(0, 31, 63, 0.1);
}

.eft-proof-modal__file {
  margin-top: 0.2rem;
  font-size: 0.8rem;
  color: rgba(0, 31, 63, 0.62);
}

.eft-proof-modal__body {
  min-height: 16rem;
  padding: 1rem;
  overflow: auto;
  background: #f8fafc;
}

.eft-proof-modal__image {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
  background: #fff;
}

.eft-proof-modal__frame {
  display: block;
  width: 100%;
  height: min(72vh, 44rem);
  border: 0;
  background: #fff;
}
</style>
